import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkInviteOptions,
  BulkSiteInviteResponse,
  ResendInviteOptions,
  SiteInviteResponse,
  bulkInvite as universalBulkInvite,
  resendInvite as universalResendInvite,
  revokeInvite as universalRevokeInvite,
} from './identity-invites-v1-site-invite-site-invites.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/user-management' };

export function bulkInvite(httpClient: HttpClient): BulkInviteSignature {
  return (
    policyIds: string[],
    options?: NonNullablePaths<BulkInviteOptions, `emails`, 2>
  ) =>
    universalBulkInvite(
      policyIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkInviteSignature {
  /**
   * Creates and sends emails inviting potential site contributors to become contributors in the requesting site.
   * > **Important**: This call requires an account level API key and cannot be authenticated with the standard authorization header. API keys are currently available to selected beta users only.
   * @param - Role IDs, referred to as policy IDs, to assign to the contributors.
   * @param - Filter options. The `emails` field **must** be passed.
   */
  (
    policyIds: string[],
    options?: NonNullablePaths<BulkInviteOptions, `emails`, 2>
  ): Promise<
    NonNullablePaths<
      BulkSiteInviteResponse,
      | `invites`
      | `invites.${number}._id`
      | `invites.${number}.siteId`
      | `invites.${number}.email`
      | `invites.${number}.inviterId`
      | `invites.${number}.status`
      | `invites.${number}.acceptLink`
      | `invites.${number}.inviterAccountId`
      | `failedEmails`,
      4
    >
  >;
}

export function resendInvite(httpClient: HttpClient): ResendInviteSignature {
  return (inviteId: string, options?: ResendInviteOptions) =>
    universalResendInvite(
      inviteId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ResendInviteSignature {
  /**
   * Resends the email invitation to a potential site contributor.
   * > **Important**: This call requires an account level API key and cannot be authenticated with the standard authorization header. API keys are currently available to selected beta users only.
   * @param - Invite ID.
   * @param - Filter options.
   */
  (inviteId: string, options?: ResendInviteOptions): Promise<
    NonNullablePaths<
      SiteInviteResponse,
      | `invite._id`
      | `invite.siteId`
      | `invite.email`
      | `invite.policyIds`
      | `invite.inviterId`
      | `invite.status`
      | `invite.acceptLink`
      | `invite.inviterAccountId`
      | `invite.locationIds`
      | `invite.callerCapabilities`,
      3
    >
  >;
}

export function revokeInvite(httpClient: HttpClient): RevokeInviteSignature {
  return (inviteId: string) =>
    universalRevokeInvite(
      inviteId,
      // @ts-ignore
      { httpClient }
    );
}

interface RevokeInviteSignature {
  /**
   * Revokes a pending site contributor invite.
   * > **Important**: This call requires an account level API key and cannot be authenticated with the standard authorization header. API keys are currently available to selected beta users only.
   * @param - Invite ID.
   */
  (inviteId: string): Promise<void>;
}

export {
  AcceptSiteInviteRequest,
  AcceptSiteInviteResponse,
  BulkInviteOptions,
  BulkSiteInviteRequest,
  BulkSiteInviteResponse,
  ContributorLimitation,
  CursorPaging,
  GetContributorLimitRequest,
  GetContributorLimitResponse,
  GetSiteInviteRequest,
  GetSiteInviteResponse,
  GetSiteInvitesRequest,
  GetSiteInvitesResponse,
  InviteStatus,
  LocationRestriction,
  Paging,
  ParseSiteInviteTokenRequest,
  ParseSiteInviteTokenResponse,
  QuerySiteInvitesRequest,
  QuerySiteInvitesResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  ResendInviteOptions,
  ResendSiteInviteRequest,
  RevokeSiteInviteRequest,
  RevokeSiteInviteResponse,
  SiteInvite,
  SiteInviteRequest,
  SiteInviteResponse,
  SortOrder,
  Sorting,
  UpdateSiteInviteRequest,
  UpdateSiteInviteResponse,
} from './identity-invites-v1-site-invite-site-invites.universal.js';
